import { useRef, useCallback, useEffect } from 'react';
import Editor, { type OnMount, loader } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { registerSolidityLanguage } from '@/lib/solidity-language';
import { registerAiInlineCompletions, registerAiCodeActions } from '@/lib/ai-completions';
import { useEditorStore } from '@/store/editorStore';

// Register Solidity language and AI completions when Monaco loads
let solidityRegistered = false;
loader.init().then((monaco) => {
  if (!solidityRegistered) {
    registerSolidityLanguage(monaco);
    registerAiInlineCompletions(monaco);
    registerAiCodeActions(monaco);
    solidityRegistered = true;
  }
});

interface CodeEditorProps {
  value: string;
  language: string;
  path: string;
  onChange: (value: string) => void;
  onSave: () => void;
}

export default function CodeEditor({
  value,
  language,
  path,
  onChange,
  onSave,
}: CodeEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const insertRequest = useEditorStore((s) => s.insertRequest);
  const clearInsertRequest = useEditorStore((s) => s.clearInsertRequest);

  // Execute pending insertText requests from the extension host
  useEffect(() => {
    if (!insertRequest || !editorRef.current) return;
    const ed = editorRef.current;
    const selection = ed.getSelection();
    if (!selection) return;
    ed.executeEdits('extension-host', [{ range: selection, text: insertRequest.text, forceMoveMarkers: true }]);
    clearInsertRequest();
  }, [insertRequest, clearInsertRequest]);

  const handleMount: OnMount = useCallback(
    (editorInstance, monaco) => {
      editorRef.current = editorInstance;

      // Register Solidity if not yet registered
      if (!solidityRegistered) {
        registerSolidityLanguage(monaco);
        solidityRegistered = true;
      }

      // Ctrl+S / Cmd+S keybinding
      editorInstance.addAction({
        id: 'save-file',
        label: 'Save File',
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
        run: () => {
          onSave();
        },
      });

      // Ctrl+Shift+P — Command Palette (built-in)
      editorInstance.addAction({
        id: 'open-command-palette',
        label: 'Open Command Palette',
        keybindings: [
          monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyP,
        ],
        run: (ed) => {
          ed.trigger('keyboard', 'editor.action.quickCommand', null);
        },
      });

      // Ctrl+D — duplicate line
      editorInstance.addAction({
        id: 'duplicate-line',
        label: 'Duplicate Line',
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyD],
        run: (ed) => {
          ed.trigger('keyboard', 'editor.action.copyLinesDownAction', null);
        },
      });

      editorInstance.focus();
    },
    [onSave]
  );

  const handleChange = useCallback(
    (val: string | undefined) => {
      if (val !== undefined) onChange(val);
    },
    [onChange]
  );

  // Map .sol files to "sol" language id
  const lang = language === 'sol' ? 'sol' : language;

  // Read settings from localStorage
  const fontSize = parseInt(localStorage.getItem('kn-ide-fontSize') || '14', 10);
  const tabSize = parseInt(localStorage.getItem('kn-ide-tabSize') || '2', 10);
  const wordWrap = localStorage.getItem('kn-ide-wordWrap') !== 'false';
  const minimap = localStorage.getItem('kn-ide-minimap') !== 'false';

  return (
    <div className="flex flex-col h-full w-full bg-[#1e1e1e]">
      <div className="flex items-center h-7 px-3 bg-[#252526] text-xs text-gray-400 border-b border-[#3c3c3c] select-none">
        <span className="truncate">{path}</span>
        {lang === 'sol' && (
          <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-purple-600/20 text-purple-400 rounded">
            Solidity
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0">
        <Editor
          height="100%"
          language={lang}
          value={value}
          path={path}
          theme="kubernodes-dark"
          onChange={handleChange}
          onMount={handleMount}
          options={{
            fontSize,
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Menlo, monospace",
            minimap: { enabled: minimap },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize,
            wordWrap: wordWrap ? 'on' : 'off',
            padding: { top: 8 },
            smoothScrolling: true,
            cursorSmoothCaretAnimation: 'on',
            renderLineHighlight: 'all',
            bracketPairColorization: { enabled: true },
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            parameterHints: { enabled: true },
            formatOnPaste: true,
            formatOnType: true,
            snippetSuggestions: 'top',
            suggest: {
              showSnippets: true,
              showKeywords: true,
              showWords: true,
            },
            inlayHints: { enabled: 'on' },
            stickyScroll: { enabled: true },
            guides: {
              bracketPairs: true,
              indentation: true,
            },
          }}
        />
      </div>
    </div>
  );
}
